/**
 * Presentation-only formatter for assessment-answer references.
 *
 * Founder-pass Finding 1: participants see compact references like
 * `s2_q3` inside AI-generated direction/profile copy. Underlying
 * identifiers and mappings must stay exactly as-is — this module only
 * normalises how those references are DISPLAYED. It:
 *
 *   - Detects `s<n>_q<n>` tokens (case-insensitive, boundary-aware).
 *   - Renders them uppercase (`S2_Q3`) in a subtle inline chip.
 *   - Announces the expanded meaning ("Section 2, Question 3") to
 *     assistive tech via aria-label + sr-only sibling.
 *
 * The regex will never invent or emit a reference for skipped, empty,
 * or malformed input — non-matching text renders unchanged.
 */

import React from "react";

// Case-insensitive match; word-boundary aware so we don't wrap parts of
// unrelated tokens like "as2_q3ish".
export const QID_RE = /\bs(\d{1,3})_q(\d{1,3})\b/gi;

/**
 * Return `{display, ariaLabel}` for a single compact qid string, or `null`
 * if the input is not a well-formed reference.
 */
export function formatQid(qid) {
  if (typeof qid !== "string") return null;
  const m = /^s(\d{1,3})_q(\d{1,3})$/i.exec(qid.trim());
  if (!m) return null;
  const s = parseInt(m[1], 10);
  const q = parseInt(m[2], 10);
  return {
    display: `S${s}_Q${q}`,
    ariaLabel: `Section ${s}, Question ${q}`,
    raw: qid.trim(),
  };
}

/**
 * Render a single QID as an inline chip. Non-interactive; renders inline
 * so it stays next to the sentence it supports.
 */
export function QidChip({ qid, testid, className = "" }) {
  const f = formatQid(qid);
  if (!f) return null;
  return (
    <span
      data-testid={testid}
      data-qid={f.raw}
      aria-label={f.ariaLabel}
      className={
        "inline-block rounded border border-border-subtle bg-bg-base " +
        "px-1.5 py-0.5 mx-0.5 text-[11px] font-mono uppercase tracking-wide " +
        "text-fg-primary align-baseline whitespace-nowrap " + className
      }
    >
      {f.display}
      <span className="sr-only"> ({f.ariaLabel})</span>
    </span>
  );
}

/**
 * Render an array of qids as a horizontal wrap of chips. Malformed
 * entries are skipped silently. Returns `null` when there is nothing
 * to render so parents can decide the empty-state copy.
 */
export function QidChipList({ qids, testidPrefix }) {
  const items = (qids || [])
    .map((q) => formatQid(q))
    .filter(Boolean);
  if (items.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 align-middle">
      {items.map((f, i) => (
        <span
          key={f.raw + ":" + i}
          data-testid={testidPrefix ? `${testidPrefix}-${f.raw}` : undefined}
          data-qid={f.raw}
          aria-label={f.ariaLabel}
          className={
            "inline-block rounded border border-border-subtle bg-bg-base " +
            "px-1.5 py-0.5 text-[11px] font-mono uppercase tracking-wide " +
            "text-fg-primary whitespace-nowrap"
          }
        >
          {f.display}
          <span className="sr-only"> ({f.ariaLabel})</span>
        </span>
      ))}
    </span>
  );
}

/**
 * Split a string on QID_RE and return React children with QidChip
 * substitutions in place of each raw match. Preserves everything else
 * verbatim. Safe to call on empty/null/non-string values.
 */
export function formatQidRefs(text, opts = {}) {
  if (text === null || text === undefined) return text;
  const s = String(text);
  if (!s) return s;
  QID_RE.lastIndex = 0;
  const out = [];
  let idx = 0;
  let m;
  let key = 0;
  while ((m = QID_RE.exec(s)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (start > idx) out.push(s.slice(idx, start));
    const secN = parseInt(m[1], 10);
    const qN = parseInt(m[2], 10);
    out.push(
      <span
        key={`qid-${key++}-${secN}-${qN}`}
        data-testid={opts.chipTestidPrefix
          ? `${opts.chipTestidPrefix}-s${secN}_q${qN}`
          : undefined}
        data-qid={`s${secN}_q${qN}`}
        aria-label={`Section ${secN}, Question ${qN}`}
        className={
          "inline-block rounded border border-border-subtle bg-bg-base " +
          "px-1.5 py-0.5 mx-0.5 text-[11px] font-mono uppercase tracking-wide " +
          "text-fg-primary align-baseline whitespace-nowrap"
        }
      >
        {`S${secN}_Q${qN}`}
        <span className="sr-only"> (Section {secN}, Question {qN})</span>
      </span>
    );
    idx = end;
  }
  if (idx === 0) return s;              // nothing matched: hand back original
  if (idx < s.length) out.push(s.slice(idx));
  return out;
}

/**
 * Static helper block shown once per page above the first evidence-
 * linked explanation. Kept short, high-contrast, readable without hover.
 */
export function EvidenceRefsHelper({ testid = "evidence-refs-helper" }) {
  return (
    <div
      data-testid={testid}
      className={
        "rounded-md border border-progress-teal/40 bg-progress-teal/10 " +
        "px-3 py-2 text-xs text-fg-primary"
      }
    >
      <span className="font-medium">How to read answer references:</span>{" "}
      S means Section and Q means Question. For example,{" "}
      <span
        className="inline-block rounded border border-border-subtle bg-bg-base px-1.5 py-0.5 text-[11px] font-mono uppercase tracking-wide text-fg-primary whitespace-nowrap"
        aria-label="Section 2, Question 3"
      >
        S2_Q3
        <span className="sr-only"> (Section 2, Question 3)</span>
      </span>{" "}
      means Section 2, Question 3.
    </div>
  );
}

export default {
  QID_RE,
  formatQid,
  formatQidRefs,
  QidChip,
  QidChipList,
  EvidenceRefsHelper,
};
